
import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { getAllOrders, getUserCount, } from "../api/apiServices";
import { FaUsers, FaDonate, FaBullhorn, FaArrowUp, FaArrowDown } from "react-icons/fa";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { useOrderAnalytics } from "../util/OrderAnalytics";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, ArcElement, Tooltip, Legend);

const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-NG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });


export default function OverviewPage() {
  const [userCount, setUserCount] = useState(null);
  const [vendorCount, setVendorCount] = useState(null);
  const [allOrders, setAllOrders] = useState(null);
  const [riderCount, setRiderCount] = useState(null);
  // const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const analytics = useOrderAnalytics(allOrders?.orders);

   useEffect(() => {
    (async () => {
      try {
        const res = await getAllOrders();
        setAllOrders(res);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getUserCount();
        setUserCount(res?.roles?.user);
        setVendorCount(res?.roles?.vendor);
        setRiderCount(res?.roles?.rider);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className="p-6 space-y-8"
    >
      {/* Hero Section */}
      <div className="border text-black rounded-xl p-8 shadow-lg">
        <h1 className="text-3xl font-bold">Welcome back, Admin 🎉</h1>
        <p className="opacity-90 mt-2">
          Here’s a quick glance at how your platform is performing today.
        </p>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Users */}
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="p-6 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm opacity-80">Total Users</p>
                  <p className="text-3xl font-bold">{userCount}</p>
                </div>
                <FaUsers size={36} />
              </div>
              {/* <p className="mt-3 text-green-300 flex items-center gap-1 text-sm">
                <FaArrowUp /> +12% this month
              </p> */}
            </motion.div>

            {/* Donations */}
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="p-6 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm opacity-80">Total Vendors</p>
                  <p className="text-3xl font-bold">
                    {vendorCount}
                  </p>
                </div>
                <FaUsers size={36} />
              </div>
              {/* <p className="mt-3 text-red-300 flex items-center gap-1 text-sm">
                <FaArrowDown /> -5% this week
              </p> */}
            </motion.div>

            {/* Campaigns */}
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="p-6 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm opacity-80">Total Riders</p>
                  <p className="text-3xl font-bold">
                    {riderCount}
                  </p>
                </div>
                <FaUsers size={36} />
              </div>
              {/* <p className="mt-3 text-green-300 flex items-center gap-1 text-sm">
                <FaArrowUp /> +3 new this week
              </p> */}
            </motion.div>
          </div>

          {/* Charts Section */}
<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
  
  {/* Orders Revenue Growth */}
  <div className="bg-white p-6 rounded-xl">
    <h3 className="text-lg font-semibold mb-4 text-gray-700">
      Orders Revenue Growth
    </h3>

    <Line
      data={{
        labels: analytics.revenueLabels,
        datasets: [
          {
            label: "Revenue (₦)",
            data: analytics.revenueData,
            borderColor: "#f97316",
            backgroundColor: "rgba(249,115,22,0.3)",
            tension: 0.4,
            fill: true,
            pointBackgroundColor: "#f97316",
          },
        ],
      }}
      options={{
        plugins: {
          legend: { labels: { color: "#374151" } },
        },
        scales: {
          x: { ticks: { color: "#374151" } },
          y: { ticks: { color: "#374151" } },
        },
      }}
    />
  </div>

  {/* Orders Status */}
  <div className="bg-white p-6 rounded-xl shadow-lg">
    <h3 className="text-lg font-semibold mb-4 text-gray-700">
      Orders Status
    </h3>

    <Pie
      data={{
        labels: analytics.statusLabels,
        datasets: [
          {
            data: analytics.statusData,
            backgroundColor: [
              "#f59e0b", // pending
              "#3b82f6", // paid
              "#10b981", // completed
              "#ef4444", // cancelled
            ],
            borderWidth: 2,
          },
        ],
      }}
      options={{
        plugins: {
          legend: { labels: { color: "#374151" } },
        },
      }}
    />
  </div>

</div>


          {/* Recent Orders */}
<div className="bg-white p-6 rounded-xl shadow-lg mt-8">
  <h3 className="text-lg text-gray-700 font-semibold mb-4">
    Recent Orders
  </h3>

  {allOrders?.orders?.length === 0 ? (
    <p className="text-gray-500 text-sm">No recent orders</p>
  ) : (
    <ul className="divide-y">
      {allOrders?.orders
        .slice(0, 5) // show latest 5
        .map((order) => (
          <li
            key={order._id}
            className="flex justify-between items-center py-4"
          >
            {/* LEFT */}
            <div>
              <p className="font-semibold text-gray-700">
                {order.orderId}
              </p>
              <p className="text-sm text-gray-500">
                {order.storeName} • {order.shippingAddress?.city}
              </p>
              <p className="text-xs text-gray-400">
                {formatDate(order.createdAt)}
              </p>
            </div>

            {/* RIGHT */}
            <div className="text-right">
              <p className="font-bold text-[#f97316]">
                ₦{order.totalAmount.toLocaleString()}
              </p>

              <span
                className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-semibold
                  ${
                    order.status === "pending"
                      ? "bg-yellow-100 text-yellow-700"
                      : order.status === "paid"
                      ? "bg-blue-100 text-blue-700"
                      : order.status === "completed"
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-600"
                  }
                `}
              >
                {order.status}
              </span>
            </div>
          </li>
        ))}
    </ul>
  )}
</div>

        </>
      )}
    </motion.div>
  );
}
